// AdminWorkspaceCreate.js
import React, { useState } from 'react';
import { Container, TextField, Button, Typography, Box, InputLabel, FormControlLabel, Checkbox } from '@mui/material';
import { createWorkspace, uploadImage } from './firebaseService';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from "../firebase"; // Firestore에 워크스페이스 추가하는 함수 import

const AdminWorkspaceCreate = () => {
    const [workspaceName, setWorkspaceName] = useState('');
    const [workspaceDescription, setWorkspaceDescription] = useState('');
    const [backgroundImageFile, setBackgroundImageFile] = useState(null);
    const [logoImageFile, setLogoImageFile] = useState(null);
    const [backgroundImage, setBackgroundImage] = useState(null);
    const [logoImage, setLogoImage] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [backgroundPreview, setBackgroundPreview] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isApprovalRequired, setIsApprovalRequired] = useState(false);
    const [guestbookBackgroundImageFile, setGuestbookBackgroundImageFile] = useState(null);
    const [guestbookBackgroundPreview, setGuestbookBackgroundPreview] = useState(null);
    const [privacyAgreement, setPrivacyAgreement] = useState('');
    const [noticeMessage, setNoticeMessage] = useState('• 욕설이나 비방은 자제해 주세요.\n• 메시지 내용은 완료 후 수정이 불가능합니다.');
    const [qrMessage, setQrMessage] = useState('메시지 작성');
    const [nameColor, setNameColor] = useState('#FFFFFF'); // 워크스페이스 이름 색상

    // 프리셋 색상 팔레트
    const presetColors = [
        '#FFFFFF', '#F0F0F0', '#FFD700', '#FFA500',
        '#87CEEB', '#98FB98', '#FFB6C1', '#FF69B4',
        '#000000', '#2C3E50', '#8B4513', '#4B0082',
        '#FF1493', '#00CED1', '#FF4500', '#32CD32'
    ];

    const handleCreateWorkspace = async () => {
        setSuccessMessage('');
        setErrorMessage('');

        if (!workspaceName.trim()) {
            setErrorMessage('워크스페이스 이름을 입력해주세요.');
            return;
        }

        try {
            setIsUploading(true);
            setUploadProgress(0);

            // Firebase Storage에 이미지 업로드
            const backgroundImageURL = backgroundImageFile
                ? await uploadImageWithProgress(backgroundImageFile, 'workspace/backgrounds')
                : null;
            const logoImageURL = logoImageFile
                ? await uploadImageWithProgress(logoImageFile, 'workspace/logos')
                : null;
            const guestbookBackgroundImageURL = guestbookBackgroundImageFile
                ? await uploadImageWithProgress(guestbookBackgroundImageFile, 'workspace/guestbookBackgrounds')
                : null;

            const workspaceData = {
                name: workspaceName,
                description: workspaceDescription,
                backgroundImage: backgroundImageURL,
                logo: logoImageURL,
                nameColor: nameColor,
                isApprovalRequired: isApprovalRequired,
                guestbookBackgroundImage: guestbookBackgroundImageURL,
                privacyAgreement: privacyAgreement,
                noticeMessage: noticeMessage,
                qrMessage: qrMessage,
            };

            const id = await createWorkspace(workspaceData);
            setSuccessMessage(`워크스페이스가 생성되었습니다 (ID: ${id})`);
            setWorkspaceName('');
            setWorkspaceDescription('');
            setBackgroundImage(null);
            setLogoImage(null);
            setBackgroundImageFile(null)
            setLogoImageFile(null)
            setLogoPreview('')
            setBackgroundPreview('')
            setGuestbookBackgroundImageFile(null)
            setGuestbookBackgroundPreview('')
            setIsApprovalRequired(false)
            setPrivacyAgreement('')
            setNoticeMessage('')
            setQrMessage('')
            setNameColor('#FFFFFF')
        } catch (error) {
            setErrorMessage('워크스페이스 생성 중 오류가 발생했습니다.');
            console.error('Error creating workspace:', error);
        }
        finally {
            setIsUploading(false);
        }
    };

    const uploadImageWithProgress = (file, folder) => {
        return new Promise((resolve, reject) => {
            const storageRef = ref(storage, `${folder}/${file.name}`);
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(progress);
                },
                (error) => {
                    console.error('Upload error:', error);
                    reject(error);
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve(downloadURL);
                }
            );
        });
    };

    const handleFileChange = (e, setFile, setPreview) => {
        const file = e.target.files[0];
        setFile(file);

        // 이미지 미리보기 설정
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setPreview(null);
        }
    };

    return (
        <Container maxWidth="sm" style={{ marginTop: '50px' }}>
            <Typography variant="h4" gutterBottom>
                워크스페이스 생성
            </Typography>
            {successMessage && (
                <Typography variant="body1" color="success.main" gutterBottom>
                    {successMessage}
                </Typography>
            )}
            {errorMessage && (
                <Typography variant="body1" color="error.main" gutterBottom>
                    {errorMessage}
                </Typography>
            )}
            <Box component="form" noValidate autoComplete="off">
                <TextField
                    label="워크스페이스 이름"
                    variant="outlined"
                    fullWidth
                    value={workspaceName}
                    onChange={(e) => setWorkspaceName(e.target.value)}
                    style={{ marginBottom: '20px' }}
                />
                <TextField
                    label="워크스페이스 설명"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={4}
                    value={workspaceDescription}
                    onChange={(e) => setWorkspaceDescription(e.target.value)}
                    style={{ marginBottom: '20px' }}
                />

                {/* 배경 이미지 업로드 */}
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} mb={2}>
                    <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="background-upload"
                        type="file"
                        onChange={(e) => handleFileChange(e, setBackgroundImageFile, setBackgroundPreview)}
                    />
                    <label htmlFor="background-upload">
                        <Button variant="contained" component="span">
                            배경 이미지 선택
                        </Button>
                    </label>
                    {backgroundImageFile && (
                        <Typography variant="body2" color="textSecondary">
                            선택된 배경 이미지: {backgroundImageFile.name}
                        </Typography>
                    )}
                    {backgroundPreview && (
                        <Box mt={2}>
                            <img src={backgroundPreview} alt="배경 이미지 미리보기" style={{ width: '100%', maxHeight: '200px', objectFit: 'cover' }} />
                        </Box>
                    )}
                </Box>

                {/* 로고 이미지 업로드 */}
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} mb={2}>
                    <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="logo-upload"
                        type="file"
                        onChange={(e) => handleFileChange(e, setLogoImageFile, setLogoPreview)}
                    />
                    <label htmlFor="logo-upload">
                        <Button variant="contained" component="span">
                            로고 이미지 선택
                        </Button>
                    </label>
                    {logoImageFile && (
                        <Typography variant="body2" color="textSecondary">
                            선택된 로고 이미지: {logoImageFile.name}
                        </Typography>
                    )}
                    {logoPreview && (
                        <Box mt={2}>
                            <img src={logoPreview} alt="로고 이미지 미리보기" style={{ width: '100%', maxHeight: '100px', objectFit: 'contain' }} />
                        </Box>
                    )}
                </Box>

                {/* 방명록 작성 화면 배경 이미지 업로드 */}
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} mb={2}>
                    <InputLabel>방명록 작성 화면 배경 이미지</InputLabel>
                    <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="guestbook-background-upload"
                        type="file"
                        onChange={(e) => handleFileChange(e, setGuestbookBackgroundImageFile, setGuestbookBackgroundPreview)}
                    />
                    <label htmlFor="guestbook-background-upload">
                        <Button variant="contained" component="span">
                            방명록 작성 배경 이미지 선택
                        </Button>
                    </label>
                    {guestbookBackgroundImageFile && (
                        <Typography variant="body2" color="textSecondary">
                            선택된 이미지: {guestbookBackgroundImageFile.name}
                        </Typography>
                    )}
                    {guestbookBackgroundPreview && (
                        <Box mt={2}>
                            <img src={guestbookBackgroundPreview} alt="방명록 작성 배경 미리보기" style={{ width: '100%', maxHeight: '200px', objectFit: 'cover' }} />
                        </Box>
                    )}
                </Box>

                <Box display="flex" flexDirection="column" alignItems="center" gap={2} mb={2}>
                    <TextField
                        label="메시지 작성 시 유의사항"
                        multiline
                        minRows={2}
                        fullWidth
                        value={noticeMessage}
                        onChange={e => setNoticeMessage(e.target.value)}
                        placeholder="방명록 작성 화면에 표시될 유의사항 문구를 입력하세요."
                    />
                    <TextField
                        label="개인정보 수집·이용 동의 내용"
                        multiline
                        minRows={4}
                        fullWidth
                        value={privacyAgreement}
                        onChange={e => setPrivacyAgreement(e.target.value)}
                        placeholder="방명록 작성 화면에 표시될 개인정보 동의 내용을 입력하세요."
                    />
                    <TextField
                        label="QR 코드 메시지"
                        fullWidth
                        value={qrMessage}
                        onChange={e => setQrMessage(e.target.value)}
                        placeholder="QR 코드에 표시될 메시지를 입력하세요."
                    />
                </Box>

                {/* 워크스페이스 이름 색상 선택 */}
                <Box display="flex" flexDirection="column" gap={2} mb={2}>
                    <Typography variant="subtitle1" gutterBottom>
                        워크스페이스 이름 색상
                    </Typography>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(8, 50px)',
                        gridTemplateRows: 'repeat(2, 50px)',
                        gap: 1,
                        justifyContent: 'center'
                    }}>
                        {presetColors.map(color => (
                            <Box
                                key={color}
                                onClick={() => setNameColor(color)}
                                sx={{
                                    width: 50,
                                    height: 50,
                                    backgroundColor: color,
                                    border: nameColor === color ? '4px solid #1976d2' : '2px solid #ccc',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        transform: 'scale(1.1)',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                    }
                                }}
                            />
                        ))}
                    </Box>

                    {/* 커스텀 색상 입력 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center', mt: 2 }}>
                        <Typography variant="body2">커스텀 색상:</Typography>
                        <input
                            type="color"
                            value={nameColor}
                            onChange={(e) => setNameColor(e.target.value)}
                            style={{
                                width: '60px',
                                height: '40px',
                                border: '2px solid #ccc',
                                borderRadius: '8px',
                                cursor: 'pointer'
                            }}
                        />
                        <TextField
                            size="small"
                            value={nameColor}
                            onChange={(e) => {
                                const value = e.target.value;
                                // Hex 색상 코드 검증 (# 포함 7자리 또는 4자리)
                                if (/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(value) || value === '#') {
                                    setNameColor(value);
                                }
                            }}
                            placeholder="#FFFFFF"
                            sx={{ width: '120px' }}
                            inputProps={{
                                style: { textTransform: 'uppercase' }
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            mt: 2,
                            p: 3,
                            backgroundColor: '#333',
                            borderRadius: '8px',
                            textAlign: 'center'
                        }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                color: nameColor,
                                fontWeight: 700,
                                fontFamily: 'GmarketSansMedium'
                            }}
                        >
                            미리보기: {workspaceName || '워크스페이스 이름'}
                        </Typography>
                    </Box>
                </Box>

                <Box display="flex" flexDirection="column" alignItems="center" gap={2} mb={2}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isApprovalRequired}
                                onChange={(e) => setIsApprovalRequired(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="메시지 승인 필요"
                    />
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateWorkspace}
                    fullWidth
                    disabled={isUploading}
                >
                    {isUploading ? '업로드 중...' : '워크스페이스 생성'}
                </Button>
            </Box>
        </Container>
    );
};

export default AdminWorkspaceCreate;
